/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { withAnalyticsContext } from '@atlaskit/analytics-next';
import Modal, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
	ModalBody,
} from '@atlaskit/modal-dialog';
import Button, { ButtonGroup } from '@atlaskit/button';
import { cssMap, jsx } from '@atlaskit/css';
import { Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type SlackWorkspace, AnalyticsSource, Cohort, ExperimentKeys } from '../../types';
import { messages } from '../i18n';
import { InstrumentedButton } from '../../analytics/InstrumentedButton';
import { useAnalytics } from '../../analytics';
import { getConnectedSlackWorkSpace } from './localStorageUtils';
import SlackWorkspacesDropdown from './SlackWorkspacesDropdown';
import DrawerDialogZIndexSetter from './DrawerDialogZIndexSetter';
import { useThirdPartyState } from './ThirdPartyContext';
import { ThirdPartyAnalytics } from './ThirdPartyAnalytics';
import { getExperimentValue } from '../../feature-gate';

const styles = cssMap({
	footerWrapper: {
		width: '100%',
	},
	disconnectButton: {
		paddingBlock: token('space.0', '0px'),
		paddingInline: token('space.0', '0px'),
		backgroundColor: token('color.background.neutral.subtle', 'transparent'),
	},
});

const SlackWorkspacesDialog = () => {
	const analytics = useAnalytics(ThirdPartyAnalytics);
	const { formatMessage } = useIntl();
	const [isSlackDisconnectDialogOpen, setIsSlackDisconnectDialogOpen] = useState(false);

	const connectedSlackWorkspaceId = getConnectedSlackWorkSpace();
	const autoConnectSlack = useMemo(() => {
		return (
			getExperimentValue(ExperimentKeys.SLACK_AUTO_CONNECT, Cohort.NOT_ENROLLED, false) ===
			Cohort.VARIATION
		);
	}, []);

	const {
		slackWorkspaces,
		openSlackConnectDialog,
		closeSlackConnectDialog,
		isSlackConnectDialogOpen,
		onChangeSelectedSlackWorkspace,
		onDisconnectSlackWorkspace,
	} = useThirdPartyState();

	const [selectedOption, setSelectedOption] = useState<SlackWorkspace | null>(null);

	useEffect(() => {
		setSelectedOption(
			slackWorkspaces.find((w) => w.id === connectedSlackWorkspaceId) || slackWorkspaces[0],
		);
	}, [slackWorkspaces, connectedSlackWorkspaceId]);

	const closeSlackDisconnectDialog = () => {
		setIsSlackDisconnectDialogOpen(false);
		openSlackConnectDialog();
	};

	const openSlackDisconnectDialog = () => setIsSlackDisconnectDialogOpen(true);

	const onDisconnectSlackLink = () => {
		analytics.slackDisconnectButtonClicked(connectedSlackWorkspaceId!);
		closeSlackConnectDialog();
		openSlackDisconnectDialog();
	};

	const onDisconnectClick = () => {
		onDisconnectSlackWorkspace();
		setIsSlackDisconnectDialogOpen(false);
	};

	const onConnectSlackWorkspaceButton = () => {
		if (selectedOption) {
			analytics.slackDoneButtonClicked(selectedOption.id);
			if (selectedOption.id !== connectedSlackWorkspaceId) {
				onChangeSelectedSlackWorkspace(selectedOption);
			}
			closeSlackConnectDialog();
		}
	};

	useEffect(() => {
		if (isSlackConnectDialogOpen) {
			analytics.slackConnectModalShown();
		}
	}, [analytics, isSlackConnectDialogOpen]);

	const DisconnectModalFooter = () => (
		<ModalFooter>
			<Inline alignBlock="end" xcss={styles.footerWrapper}>
				<ButtonGroup>
					<InstrumentedButton
						actionSubjectId="disconnectCancel"
						analyticsAttributes={{ selectedTeamId: connectedSlackWorkspaceId }}
						type="submit"
						onClick={closeSlackDisconnectDialog}
					>
						{formatMessage(messages.cancelButton)}
					</InstrumentedButton>
					<InstrumentedButton
						actionSubjectId="disconnectConfirm"
						analyticsAttributes={{ selectedTeamId: connectedSlackWorkspaceId }}
						appearance="danger"
						type="submit"
						onClick={onDisconnectClick}
					>
						{formatMessage(messages.disconnectButton)}
					</InstrumentedButton>
				</ButtonGroup>
			</Inline>
		</ModalFooter>
	);

	const ConnectModalHeader = () => {
		const { formatMessage } = useIntl();
		return (
			<ModalHeader hasCloseButton>
				<ModalTitle>
					{formatMessage(
						connectedSlackWorkspaceId
							? messages.slackWorkspacesManageDialogHeading
							: messages.slackWorkspacesConnectDialogHeading,
					)}
				</ModalTitle>
			</ModalHeader>
		);
	};

	const ConnectModalFooter = () => {
		const { formatMessage } = useIntl();

		return (
			<ModalFooter>
				{connectedSlackWorkspaceId && autoConnectSlack === false && (
					<Pressable onClick={onDisconnectSlackLink} xcss={styles.disconnectButton}>
						<Text color="color.text.danger">
							{formatMessage(messages.slackWorkspacesDialogDisconnectSlack)}
						</Text>
					</Pressable>
				)}
				<Button
					type="submit"
					appearance="primary"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ marginLeft: 'auto' }}
					isDisabled={!Boolean(selectedOption)}
					onClick={onConnectSlackWorkspaceButton}
				>
					{formatMessage(messages.slackWorkspacesDoneButton)}
				</Button>
			</ModalFooter>
		);
	};

	return (
		<>
			{slackWorkspaces && (
				<ModalTransition>
					{isSlackConnectDialogOpen && (
						<Modal
							width={'small'}
							shouldScrollInViewport
							onClose={closeSlackConnectDialog}
							testId="slack-workspace-dialog"
						>
							<ConnectModalHeader />
							<ModalBody>
								<Text as="p">{formatMessage(messages.slackWorkspacesDialogBody)}</Text>
								<br />
								<SlackWorkspacesDropdown
									slackWorkspaces={slackWorkspaces}
									selectedOption={selectedOption}
									onSelect={setSelectedOption}
								/>
								<DrawerDialogZIndexSetter />
							</ModalBody>
							<ConnectModalFooter />
						</Modal>
					)}
				</ModalTransition>
			)}
			{connectedSlackWorkspaceId && autoConnectSlack === false && (
				<ModalTransition>
					{isSlackDisconnectDialogOpen && (
						<Modal width={'small'}>
							<ModalHeader hasCloseButton>
								<ModalTitle appearance="danger">
									{formatMessage(messages.slackWorkspacesDisconnectDialogHeading)}
								</ModalTitle>
							</ModalHeader>

							<ModalBody>
								{formatMessage(messages.slackWorkspacesDisconnectDialogBody)}
								<DrawerDialogZIndexSetter />
							</ModalBody>
							<DisconnectModalFooter />
						</Modal>
					)}
				</ModalTransition>
			)}
		</>
	);
};

const analyticsContextData = {
	source: AnalyticsSource.SLACK_CONNECT_MODAL,
	componentName: 'slackConnectModal',
};

export default withAnalyticsContext(analyticsContextData)(SlackWorkspacesDialog);
